import React, { useEffect, useState } from "react";
import { Save, Users, Building2, CreditCard, Tag as TagIcon, GitBranch, ScrollText } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { PageHeader, Card, Badge, RedButton, GhostButton, Field, inputCls } from "../../components/crm/ui";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const TABS = [["org", "Organization", Building2], ["members", "Team", Users], ["pipelines", "Pipelines", GitBranch], ["subscription", "Subscription", CreditCard], ["audit", "Audit Log", ScrollText]];

export default function Settings() {
  const { org, refresh } = useAuth();
  const [tab, setTab] = useState("org");
  return (
    <div>
      <PageHeader title="Settings" subtitle="Configure your organization, team, pipelines and more." />
      <div className="flex gap-2 mb-6 overflow-x-auto scrollbar-thin">
        {TABS.map(([k, l, Ic]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`settings-tab-${k}`} className={`px-4 py-2 rounded-md text-sm font-semibold whitespace-nowrap inline-flex items-center gap-2 ${tab === k ? "bg-alto-black text-white" : "bg-white border border-slate-200 text-slate-600"}`}><Ic size={15} /> {l}</button>
        ))}
      </div>
      {tab === "org" && <OrgTab org={org} refresh={refresh} />}
      {tab === "members" && <MembersTab />}
      {tab === "pipelines" && <PipelinesTab />}
      {tab === "subscription" && <SubscriptionTab />}
      {tab === "audit" && <AuditTab />}
    </div>
  );
}

function OrgTab({ org, refresh }) {
  const [form, setForm] = useState({ name: "", type: "Business", timezone: "America/New_York", website: "", terminology: "Contact" });
  useEffect(() => { if (org) setForm({ name: org.name || "", type: org.type || "Business", timezone: org.timezone || "America/New_York", website: org.website || "", terminology: org.terminology || "Contact" }); }, [org]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => { try { await api.patch("/org", form); toast.success("Settings saved"); refresh(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <Card className="p-6 max-w-2xl space-y-4">
      <Field label="Organization name"><input className={inputCls} value={form.name} onChange={set("name")} data-testid="settings-org-name" /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Type"><select className={inputCls} value={form.type} onChange={set("type")} data-testid="settings-org-type">{["Business","Church","Real Estate","Nonprofit","Education"].map((t) => <option key={t}>{t}</option>)}</select></Field>
        <Field label="Contact terminology"><input className={inputCls} value={form.terminology} onChange={set("terminology")} data-testid="settings-terminology" placeholder="Visitor / Lead / Contact" /></Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Timezone"><input className={inputCls} value={form.timezone} onChange={set("timezone")} /></Field>
        <Field label="Website"><input className={inputCls} value={form.website} onChange={set("website")} /></Field>
      </div>
      <RedButton onClick={save} data-testid="settings-save"><Save size={15} /> Save Changes</RedButton>
    </Card>
  );
}

function MembersTab() {
  const [members, setMembers] = useState([]);
  useEffect(() => { api.get("/org/members").then(({ data }) => setMembers(data.items)).catch(() => {}); }, []);
  return (
    <Card className="divide-y divide-slate-100 max-w-2xl">
      {members.map((m) => (
        <div key={m.user_id} className="p-4 flex items-center justify-between" data-testid={`member-${m.user_id}`}>
          <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-full bg-alto-black text-white flex items-center justify-center text-sm font-bold">{(m.name || "?").slice(0,1)}</div><div><p className="font-medium text-slate-800 text-sm">{m.name}</p><p className="text-xs text-slate-500">{m.email}</p></div></div>
          <Badge tone="red">{m.role}</Badge>
        </div>
      ))}
      <div className="p-4 text-xs text-slate-400">Roles: Owner, Administrator, Manager, Team Member, Viewer. Team invitations are available on Grow and Scale plans.</div>
    </Card>
  );
}

function PipelinesTab() {
  const [pipeline, setPipeline] = useState(null);
  const [stages, setStages] = useState([]);
  useEffect(() => { api.get("/pipelines").then(({ data }) => { setPipeline(data.items[0]); setStages((data.items[0]?.stages || []).map((s) => s.name)); }); }, []);
  const save = async () => { await api.patch(`/pipelines/${pipeline.id}`, { stages: stages.filter(Boolean).map((name) => ({ name })) }); toast.success("Pipeline updated"); };
  if (!pipeline) return <div className="text-slate-400 text-sm">Loading…</div>;
  return (
    <Card className="p-6 max-w-xl">
      <h3 className="font-display font-bold text-slate-900 mb-1">{pipeline.name}</h3>
      <p className="text-sm text-slate-500 mb-4">Customize your stages (one per line). Terminology is never hard-coded.</p>
      <textarea className={inputCls + " font-mono"} rows={8} value={stages.join("\n")} onChange={(e) => setStages(e.target.value.split("\n"))} data-testid="settings-pipeline-stages" />
      <RedButton className="mt-4" onClick={save} data-testid="settings-pipeline-save"><Save size={15} /> Save Pipeline</RedButton>
    </Card>
  );
}

function SubscriptionTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/subscription").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <div className="text-slate-400 text-sm">Loading…</div>;
  const tones = { Complimentary: "green", Trial: "blue", Active: "green", "Past Due": "amber", Cancelled: "slate" };
  return (
    <Card className="p-6 max-w-xl">
      <div className="flex items-center justify-between mb-4">
        <div><p className="text-xs text-slate-500 uppercase tracking-wide">Current Plan</p><h3 className="font-display text-2xl font-bold text-slate-900">{d.plan?.name || "—"}</h3></div>
        <Badge tone={tones[d.subscription?.billing_status] || "slate"}>{d.subscription?.billing_status}</Badge>
      </div>
      {d.subscription?.reason && <p className="text-sm text-slate-600 bg-slate-50 rounded-md p-3 mb-4">{d.subscription.reason}</p>}
      <p className="text-sm font-semibold text-slate-700 mb-2">Included features</p>
      <ul className="space-y-1.5">{(d.plan?.features || []).map((f) => <li key={f} className="text-sm text-slate-600">· {f}</li>)}</ul>
    </Card>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/audit").then(({ data }) => setLogs(data.items)).catch(() => {}); }, []);
  return (
    <Card className="divide-y divide-slate-100">
      {logs.length === 0 ? <p className="p-6 text-sm text-slate-400 text-center">No audit entries yet.</p> : logs.map((l) => (
        <div key={l.id} className="p-4 flex items-center justify-between text-sm" data-testid={`audit-${l.id}`}>
          <div><span className="font-medium text-slate-800">{l.action}</span> <span className="text-slate-500">· {l.entity}</span><p className="text-xs text-slate-400 mt-0.5">{l.user_name}</p></div>
          <span className="text-xs text-slate-400">{l.created_at?.slice(0,16).replace("T"," ")}</span>
        </div>
      ))}
    </Card>
  );
}
